import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Text } from '@connect-x-app/hybrid-ui';
import { COLORS } from '@connect-x-app/shared-ui';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import { ModalOpener } from '@connect-x-app/web-ui';

import { QuickRepliesModal } from './QuickRepliesModal';
import { QuickReplyButton } from './QuickReplyButton';

import './quickReplies.scss';

type Props = {
  onPressQuickReply: (quickReply: string) => void;
  quickReplies: string[];
};

const SHOW_MORE_BUTTON_WIDTH = 50;

export const QuickReplies = ({ onPressQuickReply, quickReplies }: Props) => {
  const [visibleCount, setVisibleCount] = useState(quickReplies.length);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateButtonWidth = (button: HTMLButtonElement) => {
    const styles = window.getComputedStyle(button);
    const margin = parseInt(styles.marginLeft || '0') + parseInt(styles.marginRight || '0');
    return button.offsetWidth + margin;
  };

  const getQuickReplyButtons = (container: HTMLDivElement) => {
    return Array.from(container.querySelectorAll<HTMLButtonElement>('button:not(.show-more-button)'));
  };

  const updateVisibleCount = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const containerWidth = container.getBoundingClientRect().width;
    const buttons = getQuickReplyButtons(container);

    buttons.forEach(button => {
      button.classList.remove('hidden');
      button.classList.add('visible');
    });

    let totalWidth = 0;
    let visibleButtonCount = 0;
    const safetyBuffer = 5;

    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      const buttonWidth = calculateButtonWidth(button);

      const availableWidth = containerWidth - (buttons.length > i + 1 ? SHOW_MORE_BUTTON_WIDTH : 0) - safetyBuffer;

      if (totalWidth + buttonWidth > availableWidth) {
        break;
      }

      totalWidth += buttonWidth;
      visibleButtonCount++;
    }

    buttons.forEach((button, index) => {
      if (index < visibleButtonCount) {
        button.classList.remove('hidden');
        button.classList.add('visible');
      } else {
        button.classList.remove('visible');
        button.classList.add('hidden');
      }
    });

    setVisibleCount(visibleButtonCount);
  }, []);

  const onClickShowMore = () => {
    ModalOpener.instance.open({
      render: () => <QuickRepliesModal onPressQuickReply={onPressQuickReply} quickReplies={quickReplies} />,
    });
  };

  useLayoutEffect(() => {
    updateVisibleCount();

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateVisibleCount);
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [updateVisibleCount]);

  useEffect(() => {
    updateVisibleCount();
  }, [quickReplies, updateVisibleCount]);

  if (quickReplies.length === 0) {
    return null;
  }

  return (
    <div className="quick-reply-container" ref={containerRef}>
      <div className="quick-reply-container__quick-replies">
        {quickReplies.map((quickReply, index) => (
          <QuickReplyButton
            className={index < visibleCount ? 'visible' : 'hidden'}
            key={quickReply}
            onClick={() => onPressQuickReply(quickReply)}
            quickReply={quickReply}
          />
        ))}
        {visibleCount < quickReplies.length ? (
          <button
            aria-haspopup="dialog"
            className="quick-reply-button show-more-button"
            data-test={CHANNEL_TEST_IDS.quickReplies.showMore}
            onClick={onClickShowMore}
          >
            <Text as="span" color={COLORS.zulu.$08} size="SM" weight="bold">
              {`+${quickReplies.length - visibleCount}`}
            </Text>
          </button>
        ) : null}
      </div>
    </div>
  );
};
